import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { TeacherSidebar } from '../TeacherSidebar';
import { MenuIcon, XIcon, PlusIcon, FilterIcon, SearchIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, FilterXIcon, PencilIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { AddTaskModal } from '../AddTaskModal';
import { TaskDetailsPanel } from '../tasks/TaskDetailsPanel';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { getTaskDisplayStatus } from '../tasks/taskUtils';
import { getTeacherTasks, addTask, updateTask, deleteTask } from '../../services/taskService';
import { toast } from 'react-hot-toast';
export const TeacherTasks = () => {
  const {
    currentUser,
    loading: authLoading
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allTasks, setAllTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskPanelOpen, setIsTaskPanelOpen] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // State flag to trigger filter switch after task status update
  const [pendingFilterSwitch, setPendingFilterSwitch] = useState(false);
  // Reference for the chart
  const chartContainer = useRef(null);
  // Reference to the chart instance
  const chartInstance = useRef(null);
  // State to track which task notes are expanded
  const [expandedNoteIds, setExpandedNoteIds] = useState([]);
  // State to track fully expanded notes
  const [fullyExpandedNoteIds, setFullyExpandedNoteIds] = useState([]);
  // State to track chart filter
  const [chartFilter, setChartFilter] = useState(null);
  // NEW: Track the source of the filter (dropdown vs chart click)
  const [filterSource, setFilterSource] = useState<'dropdown' | 'chart' | null>(null);
  // Toggle note expansion
  const toggleNoteExpansion = (e, taskId) => {
    e.stopPropagation(); // Prevent opening the task details panel
    setExpandedNoteIds(prevIds => {
      // If this task is already expanded, collapse it
      if (prevIds.includes(taskId)) {
        return prevIds.filter(id => id !== taskId);
      }
      // Otherwise expand this task and collapse any others (optional requirement)
      return [taskId];
    });
    // When collapsing, also remove from fully expanded if present
    if (expandedNoteIds.includes(taskId)) {
      setFullyExpandedNoteIds(prevIds => prevIds.filter(id => id !== taskId));
    }
  };
  // Toggle full note expansion
  const toggleFullNoteExpansion = (e, taskId) => {
    e.stopPropagation(); // Prevent opening the task details panel
    setFullyExpandedNoteIds(prevIds => {
      if (prevIds.includes(taskId)) {
        return prevIds.filter(id => id !== taskId);
      }
      return [...prevIds, taskId];
    });
  };
  // Function to truncate notes text
  const truncateNotes = text => {
    if (!text || text.length <= 30) return text;
    const words = text.split(' ');
    if (words.length <= 4) return text;
    return words.slice(0, 4).join(' ') + '...';
  };
  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Load tasks from Firestore
  useEffect(() => {
    const fetchTasks = async () => {
      // CRITICAL FIX: Don't fetch if auth is still loading
      if (authLoading) {
        console.log('Auth still loading, skipping task fetch');
        return;
      }
      if (!currentUser?.uid) {
        console.log('No current user, skipping task fetch');
        setIsLoadingTasks(false);
        return;
      }
      setIsLoadingTasks(true);
      try {
        // Fetch from Firestore
        const firestoreTasks = await getTeacherTasks(currentUser.uid);
        console.log('Loaded tasks from Firestore:', firestoreTasks.length);
        setAllTasks(firestoreTasks);
        // Also sync to localStorage as backup
        const TASKS_STORAGE_KEY = 'mc_tasks_temp';
        localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(firestoreTasks));
      } catch (error) {
        console.error('Error fetching tasks from Firestore:', error);
        toast.error('Failed to load tasks');
        // Fall back to localStorage if Firestore fails
        try {
          const storedTasks = localStorage.getItem('mc_tasks_temp');
          if (storedTasks) {
            const localTasks = JSON.parse(storedTasks);
            setAllTasks(localTasks);
            console.log('Loaded tasks from localStorage backup');
          }
        } catch (localError) {
          console.error('Error loading tasks from localStorage:', localError);
        }
      } finally {
        setIsLoadingTasks(false);
      }
    };
    // Only fetch when auth is ready
    if (!authLoading) {
      fetchTasks();
    }
    // Listen for task events to refresh
    const handleTaskEvent = () => {
      if (!authLoading) {
        fetchTasks();
      }
    };
    window.addEventListener('taskCreated', handleTaskEvent);
    window.addEventListener('taskUpdated', handleTaskEvent);
    window.addEventListener('taskDeleted', handleTaskEvent);
    return () => {
      window.removeEventListener('taskCreated', handleTaskEvent);
      window.removeEventListener('taskUpdated', handleTaskEvent);
      window.removeEventListener('taskDeleted', handleTaskEvent);
    };
  }, [currentUser?.uid, authLoading]);
  // Derive filtered tasks from allTasks - ALWAYS filter from master list
  const filteredTasks = useMemo(() => {
    let filtered = [...allTasks];
    // CRITICAL: Always exclude completed tasks unless explicitly viewing them
    if (statusFilter !== 'completed') {
      filtered = filtered.filter(task => task.status !== 'completed');
    }
    // Apply search filter
    if (searchQuery) {
      const lowercaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(task => (task.title || task.name || '').toLowerCase().includes(lowercaseQuery) || (task.studentName || '').toLowerCase().includes(lowercaseQuery) || (task.notes || '').toLowerCase().includes(lowercaseQuery));
    }
    // Apply status filter for non-'all' and non-'completed' cases
    if (statusFilter !== 'all' && statusFilter !== 'completed') {
      // Use getTaskDisplayStatus for computed statuses (overdue, in-progress, pending)
      filtered = filtered.filter(task => {
        const displayStatus = getTaskDisplayStatus(task);
        return displayStatus === statusFilter;
      });
    }
    // If status === 'completed', show only completed tasks
    if (statusFilter === 'completed') {
      filtered = filtered.filter(task => task.status === 'completed');
    }
    // Sort by due date (ascending)
    filtered.sort((a, b) => {
      const dateA = new Date(a.deadline || a.dueDate);
      const dateB = new Date(b.deadline || b.dueDate);
      return dateA - dateB;
    });
    return filtered;
  }, [allTasks, searchQuery, statusFilter]);
  // React-idiomatic filter switch after task update
  useEffect(() => {
    if (pendingFilterSwitch) {
      // FIXED: Don't switch filter if currently viewing completed tasks
      // This prevents the filter from resetting when clicking completed tasks
      if (statusFilter !== 'completed') {
        // Switch to Active view after tasks state has updated
        setSearchQuery('');
        setStatusFilter('all');
        setChartFilter(null);
      }
      // Always clear the flag
      setPendingFilterSwitch(false);
    }
  }, [pendingFilterSwitch, statusFilter]);
  // Function to update chart with filtered data
  const updateChartWithFilteredData = filteredData => {
    if (chartInstance.current) {
      // Count tasks by computed status
      const taskStatusCounts = {
        completed: 0,
        'in-progress': 0,
        overdue: 0,
        pending: 0
      };
      filteredData.forEach(task => {
        const displayStatus = getTaskDisplayStatus(task);
        taskStatusCounts[displayStatus] += 1;
      });
      // Update chart data
      chartInstance.current.series[0].setData([{
        name: 'Completed',
        y: taskStatusCounts.completed,
        color: '#22c55e',
        status: 'completed' // Add status property
      }, {
        name: 'In Progress',
        y: taskStatusCounts['in-progress'],
        color: '#3b82f6',
        status: 'in-progress' // Add status property
      }, {
        name: 'Overdue',
        y: taskStatusCounts.overdue,
        color: '#ef4444',
        status: 'overdue' // Add status property
      }, {
        name: 'Pending',
        y: taskStatusCounts.pending,
        color: '#a855f7',
        status: 'pending' // Add status property
      }]);
    }
  };
  // Update chart when filtered tasks change
  useEffect(() => {
    updateChartWithFilteredData(filteredTasks);
  }, [filteredTasks]);
  // Function to open task details panel
  const openTaskDetails = task => {
    setSelectedTask(task);
    setIsTaskPanelOpen(true);
    setIsEditMode(false); // Always start in view mode
  };
  // Function to close task details panel
  const closeTaskDetails = () => {
    setIsTaskPanelOpen(false);
    setIsEditMode(false);
    setTimeout(() => {
      setSelectedTask(null);
    }, 300);
  };
  // Function to handle edit click
  const handleEditClick = () => {
    setIsEditMode(true);
  };
  // Function to handle exit edit mode
  const handleExitEditMode = () => {
    setIsEditMode(false);
  };
  // Function to handle task deletion
  const handleDeleteTask = async taskId => {
    if (!currentUser?.uid || !selectedTask?.studentId) {
      toast.error('Cannot delete task: missing information');
      return;
    }
    try {
      // Delete from Firestore
      await deleteTask(currentUser.uid, selectedTask.studentId, taskId);
      console.log('Task deleted from Firestore');
      // Remove from master list
      const updatedTasks = allTasks.filter(task => task.id !== taskId);
      setAllTasks(updatedTasks);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event
      window.dispatchEvent(new Event('taskDeleted'));
      toast.success('Task deleted successfully');
      closeTaskDetails();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };
  // Function to toggle task completion status
  const handleToggleComplete = async (task, e) => {
    e.stopPropagation(); // Prevent opening task details panel
    if (!currentUser?.uid || !task.studentId) {
      toast.error('Cannot update task: missing information');
      return;
    }
    const isCurrentlyCompleted = task.status === 'completed';
    const newStatus = isCurrentlyCompleted ? 'pending' : 'completed';
    const completedAt = isCurrentlyCompleted ? null : new Date().toISOString();
    // Store the task data needed for undo (avoid closure issues)
    const taskId = task.id;
    const studentId = task.studentId;
    const previousStatus = task.status;
    const previousCompletedAt = task.completedAt;
    // Optimistic update - update master task list
    const updatedTasks = allTasks.map(t => t.id === task.id ? {
      ...t,
      status: newStatus,
      completedAt
    } : t);
    setAllTasks(updatedTasks);
    // React-idiomatic: Set flag to trigger filter switch after tasks update
    if (isCurrentlyCompleted) {
      setPendingFilterSwitch(true);
    }
    try {
      // Update in Firestore
      await updateTask(currentUser.uid, task.studentId, task.id, {
        status: newStatus,
        completedAt
      });
      console.log('Task completion status updated in Firestore');
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event to notify other components (Dashboard)
      window.dispatchEvent(new Event('taskUpdated'));
      // Show undo toast ONLY when marking as completed
      if (newStatus === 'completed') {
        toast.success(t => <div className="flex items-center justify-between gap-3" data-id="element-9531">
              <span data-id="element-9532">Task marked as completed</span>
              <button onClick={async () => {
            toast.dismiss(t.id);
            // FIXED: Use functional state update to avoid stale closure
            setAllTasks(currentTasks => {
              return currentTasks.map(task_item => task_item.id === taskId ? {
                ...task_item,
                status: previousStatus,
                completedAt: previousCompletedAt
              } : task_item);
            });
            // Set flag to switch to Active view after undo
            setPendingFilterSwitch(true);
            try {
              // Update Firestore with reverted state
              await updateTask(currentUser.uid, studentId, taskId, {
                status: previousStatus,
                completedAt: previousCompletedAt
              });
              // Update localStorage with current state
              setAllTasks(currentTasks => {
                const TASKS_STORAGE_KEY = 'mc_tasks_temp';
                localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(currentTasks));
                return currentTasks;
              });
              // Dispatch event
              window.dispatchEvent(new Event('taskUpdated'));
              toast.success('Task restored');
            } catch (error) {
              console.error('Error undoing task completion:', error);
              toast.error('Failed to undo');
              // Revert back to completed state on error
              setAllTasks(currentTasks => {
                return currentTasks.map(task_item => task_item.id === taskId ? {
                  ...task_item,
                  status: 'completed',
                  completedAt
                } : task_item);
              });
            }
          }} className="px-3 py-1 bg-white text-gray-800 rounded-md font-medium hover:bg-gray-100 transition-colors" data-id="element-9533">
                Undo
              </button>
            </div>, {
          duration: 6000,
          style: {
            minWidth: '300px'
          }
        });
      } else {
        // Simple message when marking as incomplete
        toast.success('Task marked as incomplete');
      }
    } catch (error) {
      console.error('Error updating task completion:', error);
      // Revert optimistic update on error
      setAllTasks(allTasks);
      // Clear the pending filter switch flag on error
      setPendingFilterSwitch(false);
      toast.error('Failed to update task');
    }
  };
  // Function to handle saving edited task
  const handleSaveTask = async (taskId, updates) => {
    if (!currentUser?.uid || !selectedTask?.studentId) {
      toast.error('Cannot update task: missing information');
      return;
    }
    try {
      // Build update data object, only including dueDate if it has a value
      const updateData: any = {
        title: updates.title,
        status: updates.status,
        notes: updates.notes || '',
        reference: updates.reference || ''
      };
      // Only include dueDate if updates.dueAt has a value
      if (updates.dueAt) {
        updateData.dueDate = updates.dueAt;
      }
      // Include startDate if it has a value
      if (updates.startDate) {
        updateData.startDate = updates.startDate;
      }
      // Update in Firestore
      await updateTask(currentUser.uid, selectedTask.studentId, taskId, updateData);
      console.log('Task updated in Firestore');
      // Create a completely new task object with updated fields
      const updatedTaskData = {
        ...selectedTask,
        id: taskId,
        name: updates.title,
        title: updates.title,
        deadline: updates.dueAt || selectedTask.deadline,
        dueDate: updates.dueAt || selectedTask.dueDate,
        startDate: updates.startDate || selectedTask.startDate,
        notes: updates.notes || '',
        status: updates.status,
        reference: updates.reference || ''
      };
      // Update the task in the master list
      const updatedTasks = allTasks.map(task => {
        if (task.id === taskId) {
          return updatedTaskData;
        }
        return task;
      });
      // Update tasks state
      setAllTasks(updatedTasks);
      // CRITICAL: Update selectedTask with the exact same object reference
      // This ensures the panel receives the updated status
      setSelectedTask(updatedTaskData);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('taskUpdated'));
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };
  // Function to handle successful task addition
  const handleTaskAddSuccess = async newTask => {
    if (!currentUser?.uid) {
      toast.error('Cannot create task: not logged in');
      return;
    }
    try {
      // Add to Firestore
      const taskId = await addTask(currentUser.uid, newTask.studentId, {
        title: newTask.title,
        studentId: newTask.studentId,
        studentName: newTask.studentName,
        dueDate: newTask.dueDate,
        startDate: newTask.startDate,
        notes: newTask.notes || '',
        status: newTask.status || 'pending',
        reference: newTask.reference || ''
      });
      console.log('Task added to Firestore with ID:', taskId);
      // Add the new task to master list with Firestore ID
      const taskWithId = {
        id: taskId,
        ...newTask
      };
      const updatedTasks = [...allTasks, taskWithId];
      setAllTasks(updatedTasks);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event
      window.dispatchEvent(new Event('taskCreated'));
      toast.success('Task created successfully!');
      setIsAddTaskModalOpen(false);
    } catch (err) {
      console.error('Error adding task:', err);
      toast.error('Failed to create task');
    }
  };
  // Format date for display
  const formatDate = dateStr => {
    if (!dateStr) return 'No date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Get status badge class
  const getStatusBadgeClass = status => {
    switch (status) {
      case 'completed':
        return 'bg-[rgba(34,197,94,0.18)] border border-[rgba(34,197,94,0.45)] text-[#dff7e8]';
      case 'in-progress':
        return 'bg-[rgba(59,130,246,0.18)] border border-[rgba(59,130,246,0.45)] text-[#dbeafe]';
      default:
        return 'bg-[rgba(168,85,247,0.18)] border border-[rgba(168,85,247,0.45)] text-[#f3e8ff]';
    }
  };
  // Get status icon color
  const getStatusIconColor = status => {
    switch (status) {
      case 'completed':
        return '#22c55e';
      // green
      case 'in-progress':
        return '#3b82f6';
      // yellow
      default:
        return '#a855f7';
      // purple
    }
  };
  // Get task status class - UPDATED to use getTaskDisplayStatus utility
  const getTaskStatusClass = task => {
    const displayStatus = getTaskDisplayStatus(task);
    switch (displayStatus) {
      case 'completed':
        return 'status-completed';
      case 'overdue':
        return 'status-overdue';
      case 'in-progress':
        return 'status-inprogress';
      case 'pending':
      default:
        return 'status-pending';
    }
  };
  // Reset chart filter
  const resetChartFilter = () => {
    setChartFilter(null);
    setFilterSource(null);
    // Reset the dropdown filter to 'all' when clearing chart filter
    setStatusFilter('all');
  };
  // Handle document click to reset filter when clicking outside the chart
  useEffect(() => {
    const handleDocumentClick = e => {
      // FIXED: Only reset if filter was initiated by chart click, not dropdown
      if (!chartFilter || filterSource !== 'chart') return;
      // Check if click is outside the chart container
      if (chartContainer.current && !chartContainer.current.contains(e.target)) {
        resetChartFilter();
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [chartFilter, filterSource]);
  // Open sidebar by default on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    // Set initial state based on screen size
    handleResize();
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Load Highcharts and initialize the donut chart
  useEffect(() => {
    // Count tasks by status - UPDATED to use getTaskDisplayStatus utility
    const taskStatusCounts = {
      completed: 0,
      'in-progress': 0,
      overdue: 0,
      pending: 0
    };
    // Count tasks by computed status from ALL tasks
    allTasks.forEach(task => {
      const displayStatus = getTaskDisplayStatus(task);
      taskStatusCounts[displayStatus] += 1;
    });
    // Load Highcharts scripts
    const loadHighcharts = () => {
      return new Promise(resolve => {
        // Check if Highcharts is already loaded
        if (window.Highcharts) {
          resolve();
          return;
        }
        // Load Highcharts core
        const script1 = document.createElement('script');
        script1.src = 'https://code.highcharts.com/highcharts.js';
        script1.async = true;
        document.body.appendChild(script1);
        script1.onload = () => {
          // Load Highcharts 3D module
          const script2 = document.createElement('script');
          script2.src = 'https://code.highcharts.com/highcharts-3d.js';
          script2.async = true;
          document.body.appendChild(script2);
          script2.onload = () => {
            resolve();
          };
        };
      });
    };
    // Initialize the chart
    const initChart = () => {
      if (!window.Highcharts || !chartContainer.current) return;
      // Check if chart already exists
      if (chartInstance.current) {
        // Update existing chart with new data
        updateChartWithFilteredData(allTasks);
        return;
      }
      // Initialize new chart
      chartInstance.current = window.Highcharts.chart('assigned-tasks-donut', {
        chart: {
          type: 'pie',
          backgroundColor: 'transparent',
          options3d: {
            enabled: true,
            alpha: 45,
            beta: 0
          },
          animation: true,
          height: 280
        },
        title: {
          text: null
        },
        credits: {
          enabled: false
        },
        tooltip: {
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)'
        },
        plotOptions: {
          pie: {
            innerSize: 100,
            depth: 45,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              distance: 12,
              format: '{point.name}',
              style: {
                textOutline: 'none',
                fontSize: '12px',
                fontWeight: '500',
                color: isDark ? '#fff' : '#333'
              }
            },
            point: {
              events: {
                click: function () {
                  // Get the status directly from the point's status property
                  const filterStatus = this.status;
                  // Update both filters to ensure consistency
                  setStatusFilter(filterStatus);
                  setChartFilter(filterStatus);
                  // NEW: Mark this as a chart-initiated filter
                  setFilterSource('chart');
                }
              }
            }
          },
          series: {
            animation: {
              duration: 450
            }
          }
        },
        series: [{
          name: 'Tasks',
          data: [{
            name: 'Completed',
            y: taskStatusCounts.completed,
            color: '#22c55e',
            status: 'completed' // Add status property
          }, {
            name: 'In Progress',
            y: taskStatusCounts['in-progress'],
            color: '#3b82f6',
            status: 'in-progress' // Add status property
          }, {
            name: 'Overdue',
            y: taskStatusCounts.overdue,
            color: '#ef4444',
            status: 'overdue' // Add status property
          }, {
            name: 'Pending',
            y: taskStatusCounts.pending,
            color: '#a855f7',
            status: 'pending' // Add status property
          }]
        }]
      });
    };
    // Load scripts and initialize chart
    loadHighcharts().then(() => {
      initChart();
    });
    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [allTasks, isDark]);
  // Filter tasks based on chart filter for display
  const displayTasks = filteredTasks;
  return <div className={`w-full min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-9534">
      {/* Status Color Variables */}
      <style jsx global data-id="element-9535">{`
        :root {
          --status-completed: #22c55e;
          --status-inprogress: #3b82f6;
          --status-overdue: #ef4444;
          --status-pending: #a855f7;
          --surface-1: rgba(255, 255, 255, 0.06);
          --text-strong: #e6ebf5;
          --text-muted: #a9b2c3;
          --notes-toggle-dark: #3b82f6;
          --notes-toggle-light: #3b82f6;
          --notes-toggle-hover-dark: #60a5fa;
          --notes-toggle-hover-light: #2563eb;
        }

        .task-row {
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease,
            background-color 0.2s ease;
          border-radius: 10px;
          margin-bottom: 8px;
          will-change: transform;
          transform: translateZ(0);
          backface-visibility: hidden;
        }

        html:not(.dark) .task-row {
          border: 1.5px solid #9ca3af;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
          background-color: white;
        }

        html:not(.dark) .task-row:hover {
          transform: translateY(-2px);
          border-color: #6b7280;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        html.dark .task-row {
          border: 1.5px solid rgba(255, 255, 255, 0.25);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        html.dark .task-row:hover {
          transform: translateY(-2px);
          background: var(--surface-1);
          border-color: rgba(255, 255, 255, 0.4);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
        }

        .status-dot {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          display: inline-block;
          flex-shrink: 0;
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15) inset;
        }

        .status-completed .status-dot {
          background: var(--status-completed);
        }

        html.dark .status-completed .status-pill {
          color: #dff7e8;
          background: rgba(34, 197, 94, 0.18);
          border: 1px solid rgba(34, 197, 94, 0.45);
        }

        html:not(.dark) .status-completed .status-pill {
          color: #166534;
          background: #dcfce7;
          border: 1px solid rgba(34, 197, 94, 0.45);
        }

        .status-inprogress .status-dot {
          background: var(--status-inprogress);
        }

        html.dark .status-inprogress .status-pill {
          color: #dbeafe;
          background: rgba(59, 130, 246, 0.18);
          border: 1px solid rgba(59, 130, 246, 0.45);
        }

        html:not(.dark) .status-inprogress .status-pill {
          color: #1e3a8a;
          background: #dbeafe;
          border: 1px solid rgba(59, 130, 246, 0.45);
        }

        .status-overdue .status-dot {
          background: var(--status-overdue);
        }

        html.dark .status-overdue .status-pill {
          color: #ffe3e3;
          background: rgba(239, 68, 68, 0.18);
          border: 1px solid rgba(239, 68, 68, 0.45);
        }

        html:not(.dark) .status-overdue .status-pill {
          color: #991b1b;
          background: #fee2e2;
          border: 1px solid rgba(239, 68, 68, 0.45);
        }

        .status-pending .status-dot {
          background: var(--status-pending);
        }

        html.dark .status-pending .status-pill {
          color: #f3e8ff;
          background: rgba(168, 85, 247, 0.18);
          border: 1px solid rgba(168, 85, 247, 0.45);
        }

        html:not(.dark) .status-pending .status-pill {
          color: #581c87;
          background: #f3e8ff;
          border: 1px solid rgba(168, 85, 247, 0.45);
        }

        html:not(.dark) .task-row:hover {
          background: rgba(0, 0, 0, 0.02);
        }

        .notes-toggle {
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          margin-top: 4px;
          transition: color 0.2s ease;
        }

        html.dark .notes-toggle {
          color: var(--notes-toggle-dark);
        }

        html:not(.dark) .notes-toggle {
          color: var(--notes-toggle-light);
        }

        html.dark .notes-toggle:hover {
          color: var(--notes-toggle-hover-dark);
        }

        html:not(.dark) .notes-toggle:hover {
          color: var(--notes-toggle-hover-light);
        }

        .notes-content {
          font-size: 14px;
          font-weight: normal;
          margin-top: 4px;
          overflow: hidden;
        }

        html.dark .notes-content {
          color: #e0e0e0;
        }

        html:not(.dark) .notes-content {
          color: #444444;
        }

        .notes-truncated {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .show-full-toggle {
          font-size: 13px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          transition: color 0.2s ease;
        }

        html.dark .show-full-toggle {
          color: var(--notes-toggle-dark);
        }

        html:not(.dark) .show-full-toggle {
          color: var(--notes-toggle-light);
        }

        html.dark .show-full-toggle:hover {
          color: var(--notes-toggle-hover-dark);
        }

        html:not(.dark) .show-full-toggle:hover {
          color: var(--notes-toggle-hover-light);
        }
      `}</style>

      {/* Show loading state */}
      {isLoadingTasks && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-9536">
          <div className={`${isDark ? 'bg-navy-800' : 'bg-white'} rounded-lg p-6`} data-id="element-9537">
            <p className={isDark ? 'text-white' : 'text-gray-800'} data-id="element-9538">
              Loading tasks...
            </p>
          </div>
        </div>}

      <Navbar data-id="element-9539" />

      <div className="pt-20 pb-16 flex flex-col lg:flex-row" data-id="element-9540">
        {/* Mobile sidebar toggle */}
        <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-3 rounded-full shadow-lg flex items-center justify-center" aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'} style={{
        minWidth: '56px',
        minHeight: '56px'
      }} data-id="element-9541">
          {isSidebarOpen ? <XIcon className="h-6 w-6" data-id="element-9542" /> : <MenuIcon className="h-6 w-6" data-id="element-9543" />}
        </button>

        {/* Left Sidebar */}
        <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${isDark ? 'bg-navy-800 shadow-lg' : 'bg-white shadow-md'} z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-9544">
          <TeacherSidebar darkMode={isDark} closeSidebar={() => setIsSidebarOpen(false)} data-id="element-9545" />
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'} px-4 py-6`} data-id="element-9546">
          <div className="mx-auto" data-id="element-9547">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-9548">
              <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'} mb-4 sm:mb-0`} data-id="element-9549">
                Assigned Tasks
              </h1>
              <button onClick={() => setIsAddTaskModalOpen(true)} className={`px-4 py-2 rounded-md border-2 transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-9550">
                Create New Task
              </button>
            </div>

            {/* Filters row */}
            <div className="grid grid-cols-12 gap-4 mb-6" data-id="element-9551">
              <div className="col-span-12 md:col-span-8" id="filters-left" data-id="element-9552">
                <div className={`flex items-center rounded-lg px-3 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-9553">
                  <SearchIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9554" />
                  <input type="text" placeholder="Search tasks..." className={`w-full p-3 bg-transparent border-none focus:ring-0 focus:outline-none ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-800 placeholder-gray-500'}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-9555" />
                </div>
              </div>
              <div className="col-span-12 md:col-span-4" id="filters-right" data-id="element-9556">
                <div className="flex items-center" data-id="element-9557">
                  <select className={`w-full p-3 rounded-lg border-none focus:ring-0 ${isDark ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} value={statusFilter} onChange={e => {
                  const newStatus = e.target.value;
                  setStatusFilter(newStatus);
                  setChartFilter(newStatus === 'all' ? null : newStatus);
                  // NEW: Mark this as a dropdown-initiated filter
                  setFilterSource(newStatus === 'all' ? null : 'dropdown');
                }} data-id="element-9558">
                    <option value="all" data-id="element-9559">Active Tasks</option>
                    <option value="pending" data-id="element-9560">Pending</option>
                    <option value="in-progress" data-id="element-9561">In Progress</option>
                    <option value="completed" data-id="element-9562">Completed</option>
                    <option value="overdue" data-id="element-9563">Overdue</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Content row */}
            <div className="grid grid-cols-12 gap-4" data-id="element-9564">
              <div className="col-span-12 md:col-span-8" id="content-left" data-id="element-9565">
                <div className={`rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm p-6`} data-id="element-9566">
                  <div className="flex justify-between items-center mb-4" data-id="element-9567">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9568">
                      {displayTasks.length}{' '}
                      {displayTasks.length === 1 ? 'Task' : 'Tasks'}{' '}
                      {chartFilter && <span className="ml-1" data-id="element-9569">
                          (
                          {chartFilter === 'in-progress' ? 'In Progress' : chartFilter.charAt(0).toUpperCase() + chartFilter.slice(1)}
                          )
                        </span>}
                    </h2>
                    {/* Show active filter badge if a chart filter is applied */}
                    {chartFilter && <div className="flex items-center" data-id="element-9570">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium mr-2 ${chartFilter === 'completed' ? 'bg-green-100 text-green-800' : chartFilter === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : chartFilter === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-9571">
                          Filtered by:{' '}
                          {chartFilter === 'in-progress' ? 'In Progress' : chartFilter.charAt(0).toUpperCase() + chartFilter.slice(1)}
                        </span>
                        <button onClick={resetChartFilter} className={`p-1 rounded-full ${isDark ? 'bg-navy-700 hover:bg-navy-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`} title="Clear filter" data-id="element-9572">
                          <XIcon className="h-4 w-4" data-id="element-9573" />
                        </button>
                      </div>}
                  </div>

                  {displayTasks.length > 0 ? <div className="space-y-3" data-id="element-9574">
                      {displayTasks.map(task => {
                    // Determine status class
                    const statusClass = getTaskStatusClass(task);
                    // Check if notes are expanded for this task
                    const isNotesExpanded = expandedNoteIds.includes(task.id);
                    // Check if notes are fully expanded for this task
                    const isNotesFullyExpanded = fullyExpandedNoteIds.includes(task.id);
                    // Check if task has notes
                    const hasNotes = task.notes && task.notes.trim().length > 0;
                    return <div key={task.id} className={`task-row ${statusClass} px-4 py-3 cursor-pointer`} onClick={() => openTaskDetails(task)} data-id="element-9575">
                            {/* Mobile Layout: Stacked Rows */}
                            <div className="flex md:hidden" data-id="element-9576">
                              {/* Task Content */}
                              <div className="flex-1 flex flex-col gap-2" data-id="element-9577">
                                {/* Row 1: Student */}
                                <div className="flex justify-between items-center" data-id="element-9578">
                                  <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9579">
                                    Student
                                  </span>
                                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9580">
                                    {task.studentName || 'No student assigned'}
                                  </span>
                                </div>

                                {/* Row 2: Task */}
                                {/* Only show task title when notes are NOT expanded */}
                                {!isNotesExpanded && <div className="flex justify-between items-center" data-id="element-9581">
                                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9582">
                                      Task
                                    </span>
                                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} text-right`} data-id="element-9583">
                                      {task.title || task.name}
                                    </span>
                                  </div>}

                                {/* Row 3: Date */}
                                <div className="flex justify-between items-center" data-id="element-9584">
                                  <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9585">
                                    Date
                                  </span>
                                  <span className={`text-sm ${getTaskDisplayStatus(task) === 'overdue' ? 'text-red-500 font-medium' : isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9586">
                                    {formatDate(task.deadline || task.dueDate)}
                                  </span>
                                </div>

                                {/* Row 4: Status */}
                                <div className="flex justify-between items-center" data-id="element-9587">
                                  <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9588">
                                    Status
                                  </span>
                                  <span className="status-pill inline-block px-3 py-1 rounded-full text-xs font-medium" data-id="element-9589">
                                    {(() => {
                                const displayStatus = getTaskDisplayStatus(task);
                                switch (displayStatus) {
                                  case 'completed':
                                    return 'Completed';
                                  case 'overdue':
                                    return 'Overdue';
                                  case 'in-progress':
                                    return 'In Progress';
                                  case 'pending':
                                  default:
                                    return 'Pending';
                                }
                              })()}
                                  </span>
                                </div>

                                {/* Notes section - below main rows */}
                                {hasNotes && <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-9590">
                                    {!isNotesExpanded ? <button className="notes-toggle" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-9591">
                                        Show Notes{' '}
                                        <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-9592" />
                                      </button> : <div data-id="element-9593">
                                        <div className={`notes-content ${!isNotesFullyExpanded ? 'notes-truncated' : ''}`} data-id="element-9594">
                                          {task.notes}
                                        </div>
                                        {task.notes.length > 120 && !isNotesFullyExpanded && <button className="show-full-toggle mt-1" onClick={e => toggleFullNoteExpansion(e, task.id)} data-id="element-9595">
                                              Show Full
                                            </button>}
                                        <button className="notes-toggle mt-2" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-9596">
                                          Hide Notes{' '}
                                          <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-9597" />
                                        </button>
                                      </div>}
                                  </div>}

                                {/* Checkbox - moved to bottom-right */}
                                <div className="flex justify-end mt-2" data-id="element-9598">
                                  <button onClick={e => handleToggleComplete(task, e)} className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:ring-offset-2 rounded-full" aria-label={task.status === 'completed' ? 'Mark task as incomplete' : 'Mark task as complete'} data-id="element-9599">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${task.status === 'completed' ? 'bg-[#10B981] border-[#10B981]' : isDark ? 'border-gray-500 hover:border-[#10B981] hover:bg-[#10B981]/10' : 'border-gray-400 hover:border-[#10B981] hover:bg-[#10B981]/10'}`} data-id="element-9600">
                                      {task.status === 'completed' && <CheckCircleIcon className="h-3.5 w-3.5 text-white" data-id="element-9601" />}
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Desktop Layout: Grid with Checkbox */}
                            <div className="hidden md:grid" style={{
                        gridTemplateColumns: '1.5fr 1.8fr 1.2fr 1.2fr 40px',
                        columnGap: '20px',
                        alignItems: 'start'
                      }} data-id="element-9602">
                              {/* Column 1: Student */}
                              <div className="task-student" data-id="element-9603">
                                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9604">
                                  Student
                                </p>
                                <p className={`task-subtitle text-sm leading-snug ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9605">
                                  {task.studentName ? <span data-id="element-9606">{task.studentName}</span> : 'No student assigned'}
                                </p>
                              </div>

                              {/* Column 2: Task */}
                              <div className="task-content" data-id="element-9607">
                                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9608">
                                  Task
                                </p>
                                {/* Only show truncated title when notes are NOT expanded */}
                                {!isNotesExpanded && <h3 className={`task-title text-sm leading-snug ${isDark ? 'text-gray-300' : 'text-gray-600'}`} title={task.title || task.name} data-id="element-9609">
                                    {(task.title || task.name).length > 18 ? (task.title || task.name).substring(0, 18) + '…' : task.title || task.name}
                                  </h3>}
                                {/* Notes toggle button - only show if task has notes */}
                                {hasNotes && <>
                                    {!isNotesExpanded ? <button className="notes-toggle" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-9610">
                                        Show Notes{' '}
                                        <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-9611" />
                                      </button> : <div className="mt-2" data-id="element-9612">
                                        <div className={`notes-content ${!isNotesFullyExpanded ? 'notes-truncated' : ''}`} data-id="element-9613">
                                          {task.notes}
                                        </div>
                                        {/* Show Full toggle - only if notes are long enough to truncate and not fully expanded */}
                                        {task.notes.length > 120 && !isNotesFullyExpanded && <button className="show-full-toggle mt-1" onClick={e => toggleFullNoteExpansion(e, task.id)} data-id="element-9614">
                                              Show Full
                                            </button>}
                                        <button className="notes-toggle mt-2" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-9615">
                                          Hide Notes{' '}
                                          <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-9616" />
                                        </button>
                                      </div>}
                                  </>}
                              </div>

                              {/* Column 3: Date */}
                              <div className="task-date" data-id="element-9617">
                                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9618">
                                  Date
                                </p>
                                <p className={`text-sm leading-snug ${getTaskDisplayStatus(task) === 'overdue' ? 'text-red-500 font-medium' : isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9619">
                                  {formatDate(task.deadline || task.dueDate)}
                                </p>
                              </div>

                              {/* Column 4: Status */}
                              <div className="task-status" data-id="element-9620">
                                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9621">
                                  Status
                                </p>
                                <span className="status-pill inline-block px-3 py-1 rounded-full text-xs font-medium" data-id="element-9622">
                                  {(() => {
                              const displayStatus = getTaskDisplayStatus(task);
                              switch (displayStatus) {
                                case 'completed':
                                  return 'Completed';
                                case 'overdue':
                                  return 'Overdue';
                                case 'in-progress':
                                  return 'In Progress';
                                case 'pending':
                                default:
                                  return 'Pending';
                              }
                            })()}
                                </span>
                              </div>

                              {/* Checkbox Column - moved to bottom-right */}
                              <div className="flex items-end justify-end" data-id="element-9623">
                                <button onClick={e => handleToggleComplete(task, e)} className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:ring-offset-2 rounded-full" aria-label={task.status === 'completed' ? 'Mark task as incomplete' : 'Mark task as complete'} data-id="element-9624">
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${task.status === 'completed' ? 'bg-[#10B981] border-[#10B981]' : isDark ? 'border-gray-500 hover:border-[#10B981] hover:bg-[#10B981]/10' : 'border-gray-400 hover:border-[#10B981] hover:bg-[#10B981]/10'}`} data-id="element-9625">
                                    {task.status === 'completed' && <CheckCircleIcon className="h-3.5 w-3.5 text-white" data-id="element-9626" />}
                                  </div>
                                </button>
                              </div>
                            </div>
                          </div>;
                  })}
                    </div> : <div className={`py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9627">
                      <p className="text-lg" data-id="element-9628">No tasks found</p>
                      <p className="mt-2" data-id="element-9629">
                        {searchQuery || statusFilter !== 'all' || chartFilter ? 'Try adjusting your filters' : 'Create a new task to get started'}
                      </p>
                    </div>}
                </div>
              </div>

              <div className="col-span-12 md:col-span-4" id="content-right" data-id="element-9630">
                {/* Tasks by Status Donut Chart */}
                <div className={`rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm p-6 mb-6`} data-id="element-9631">
                  <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-9632">
                    Tasks by Status
                  </h3>
                  <div id="assigned-tasks-donut" ref={chartContainer} className="w-full max-w-full mx-auto md:w-[420px]" style={{
                  height: '280px',
                  margin: '0 auto'
                }} data-id="element-9633"></div>
                  {/* Show All button */}
                  <div className="mt-2 text-center" data-id="element-9634">
                    <button onClick={resetChartFilter} className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-all duration-300 transform hover:-translate-y-0.5 ${isDark ? 'bg-navy-700 text-white hover:bg-navy-600 hover:text-red-300' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-red-600'}`} data-id="element-9635">
                      Show All Tasks
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Overlay for mobile when sidebar is open */}
        {isSidebarOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleSidebar} aria-hidden="true" data-id="element-9636"></div>}
      </div>

      {/* Add Task Modal */}
      {isAddTaskModalOpen && <AddTaskModal isOpen={isAddTaskModalOpen} onClose={() => setIsAddTaskModalOpen(false)} onAddTask={handleTaskAddSuccess} darkMode={isDark} teacherId={currentUser?.uid} data-id="element-9637" />}

      {/* Task Details Panel */}
      <TaskDetailsPanel open={isTaskPanelOpen} task={selectedTask} onClose={closeTaskDetails} onEditClick={handleEditClick} onExitEditMode={handleExitEditMode} onDeleteClick={handleDeleteTask} onSaveEdit={handleSaveTask} darkMode={isDark} isEditMode={isEditMode} data-id="element-9638" />

      <Footer data-id="element-9639" />
    </div>;
};